const PERMISSION_ALIASES = {
  modify_records: [
    "edit_own_hoardings", "edit_assigned_hoardings", "edit_all_hoardings",
    "edit_own_campaigns", "edit_assigned_campaigns", "edit_all_campaigns",
    "edit_own_clients", "edit_assigned_clients", "edit_all_clients",
    "update_hoarding_availability", "upload_hoarding_photos",
  ],
  delete_records: ["delete_hoardings", "delete_campaigns", "delete_clients"],
  view_pnl: ["view_profit_and_loss", "view_financial_reports"],
};

export const PERMISSION_LABELS = {
  view_hoardings: "View hoardings",
  create_hoardings: "Create hoardings",
  edit_own_hoardings: "Edit own hoardings",
  edit_assigned_hoardings: "Edit assigned hoardings",
  edit_all_hoardings: "Edit all hoardings",
  upload_hoarding_photos: "Upload hoarding photos",
  update_hoarding_availability: "Update availability",
  delete_hoardings: "Delete hoardings",
  view_own_campaigns: "View own campaigns",
  view_assigned_campaigns: "View assigned campaigns",
  view_all_campaigns: "View all campaigns",
  create_campaigns: "Create campaigns",
  edit_own_campaigns: "Edit own campaigns",
  edit_assigned_campaigns: "Edit assigned campaigns",
  edit_all_campaigns: "Edit all campaigns",
  assign_hoardings_to_campaign: "Assign hoardings to campaigns",
  change_campaign_dates: "Change campaign dates",
  change_campaign_amounts: "Change campaign amounts",
  delete_campaigns: "Delete campaigns",
  view_own_clients: "View own clients",
  view_assigned_clients: "View assigned clients",
  view_all_clients: "View all clients",
  create_clients: "Create clients",
  edit_own_clients: "Edit own clients",
  edit_assigned_clients: "Edit assigned clients",
  edit_all_clients: "Edit all clients",
  view_client_contact_details: "View client contact details",
  delete_clients: "Delete clients",
  view_basic_revenue: "View basic revenue",
  view_campaign_amounts: "View campaign amounts",
  edit_campaign_amounts: "Edit campaign amounts",
  view_rent_details: "View rent details",
  edit_rent_details: "Edit rent details",
  view_expenses: "View expenses",
  create_expenses: "Create expenses",
  edit_expenses: "Edit expenses",
  delete_expenses: "Delete expenses",
  view_profit_and_loss: "View P&L",
  view_financial_reports: "View financial reports",
  export_reports: "Export reports",
  view_ai_insights: "View AI insights",
  view_city_analytics: "View city analytics",
  view_proposal_flows: "View proposal flows",
  manage_proposal_flows: "Manage proposal flows",
  manage_partner_network: "Manage Partner Network",
  view_accounts: "View accounts",
  manage_accounts: "Manage accounts",
  view_invoices: "View invoices",
  manage_invoices: "Manage invoices",
  view_payments: "View payments",
  manage_payments: "Manage payments",
  view_export_templates: "View export templates",
  manage_export_templates: "Manage export templates",
  export_accounts_data: "Export accounts data",
  view_gst_summary: "View GST summary",
  invite_employees: "Invite employees",
  edit_employee_permissions: "Edit employee permissions",
  deactivate_employees: "Deactivate employees",
  assign_inventory_to_employees: "Assign inventory to employees",
  view_audit_logs: "View audit logs",
};

export function permissionLabel(permission) {
  return PERMISSION_LABELS[permission] || permission.replace(/_/g, " ");
}

export function hasPermission(user, permission) {
  if (!user) return false;
  if (user.role === "super_admin" || user.role === "advertiser_admin") return true;
  if (permission === "view_ai_insights" && user.access_profile?.scope_type !== "all_inventory") return false;
  const permissions = user.permissions || {};
  if (permissions[permission]) return true;
  return (PERMISSION_ALIASES[permission] || []).some((alias) => permissions[alias]);
}

export function hasAnyPermission(user, permissions = []) {
  if (!user) return false;
  if (user.role === "super_admin" || user.role === "advertiser_admin") return true;
  return permissions.some((permission) => hasPermission(user, permission));
}

export const ACCESS = {
  dashboard: [
    "view_hoardings", "view_basic_revenue", "view_campaign_amounts",
    "view_profit_and_loss", "view_financial_reports", "view_ai_insights",
  ],
  cityAnalytics: ["view_city_analytics", "view_hoardings", "view_basic_revenue"],
  hoardings: ["view_hoardings", "create_hoardings"],
  campaigns: ["view_own_campaigns", "view_assigned_campaigns", "view_all_campaigns", "create_campaigns"],
  proposals: ["view_hoardings", "create_campaigns", "export_reports"],
  partnerNetwork: ["manage_partner_network"],
  proposalArrangements: ["view_proposal_flows", "manage_proposal_flows", "create_campaigns", "export_reports"],
  clients: ["view_own_clients", "view_assigned_clients", "view_all_clients", "create_clients"],
  analysis: ["view_basic_revenue", "view_campaign_amounts", "view_profit_and_loss", "view_financial_reports"],
  insights: ["view_ai_insights"],
  financeOps: ["view_basic_revenue", "view_campaign_amounts", "view_rent_details", "view_expenses", "view_profit_and_loss", "view_financial_reports"],
  accounts: ["view_accounts", "manage_accounts", "view_invoices", "manage_invoices", "view_payments", "manage_payments", "view_export_templates", "manage_export_templates", "export_accounts_data", "view_gst_summary", "view_financial_reports"],
  reports: ["view_financial_reports", "export_reports"],
  branches: ["invite_employees", "edit_employee_permissions", "view_financial_reports", "export_reports"],
  employees: ["invite_employees", "edit_employee_permissions", "deactivate_employees", "assign_inventory_to_employees"],
  audit: ["view_audit_logs"],
  business: ["create_hoardings", "edit_all_hoardings", "modify_records", "edit_campaign_amounts", "change_campaign_amounts", "manage_partner_network", "invite_employees", "edit_employee_permissions", "view_financial_reports", "export_reports"],
};

export function defaultPathForUser(user) {
  if (!user) return "/";
  if (user.role === "super_admin") return "/super-admin";

  const destinations = [
    ["/dashboard", ACCESS.dashboard],
    ["/hoardings", ACCESS.hoardings],
    ["/campaigns", ACCESS.campaigns],
    ["/finance", ACCESS.financeOps],
    ["/accounts", ACCESS.accounts],
    ["/proposals/create", ACCESS.proposals],
    ["/clients", ACCESS.clients],
    ["/reports", ACCESS.reports],
    ["/employees", ACCESS.employees],
    ["/business", ACCESS.business],
  ];
  return destinations.find(([, permissions]) => hasAnyPermission(user, permissions))?.[0] || "/profile";
}
